import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { setAuthToken } from '@/lib/api/axiosConfig';
import { updateAuthState } from '@/lib/api/baseApi';

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: any;
    getToken: () => Promise<string | null>;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const { isSignedIn, isLoaded, getToken, signOut } = useAuth();
    const { user } = useUser();

    // Sync auth state with baseAPI
    useEffect(() => {
        updateAuthState({
            isLoaded,
            isSignedIn: isSignedIn || false,
            getToken: isSignedIn ? getToken : null,
        });
    }, [isSignedIn, isLoaded, getToken]);

    // Set token in axios interceptor whenever auth state changes
    useEffect(() => {
        const updateToken = async () => {
            if (isSignedIn && isLoaded) {
                try {
                    const token = await getToken();
                    if (token) {
                        setAuthToken(token);
                    }
                } catch (error) {
                    setAuthToken(null);
                }
            } else {
                setAuthToken(null);
            }
        };

        updateToken();
    }, [isSignedIn, isLoaded, getToken]);

    // Refresh token every 50 seconds (before Clerk's 60-second expiration)
    useEffect(() => {
        if (!isSignedIn || !isLoaded) return;

        const refreshToken = async () => {
            try {
                const token = await getToken({ skipCache: true });
                if (token) {
                    setAuthToken(token);
                }
            } catch (error) {
                // Token refresh failed - will be handled by next request
            }
        };

        const interval = setInterval(refreshToken, 50000); // 50 seconds

        return () => clearInterval(interval);
    }, [isSignedIn, isLoaded, getToken]);

    const handleGetToken = async (): Promise<string | null> => {
        try {
            return await getToken();
        } catch (error) {
            return null;
        }
    };

    const handleSignOut = async (): Promise<void> => {
        try {
            await signOut();
            setAuthToken(null);
            updateAuthState({
                isLoaded: true,
                isSignedIn: false,
                getToken: null,
            });
        } catch (error) {
            // Sign out failed - will be handled by UI
        }
    };

    const value: AuthContextType = {
        isAuthenticated: isSignedIn || false,
        isLoading: !isLoaded,
        user,
        getToken: handleGetToken,
        signOut: handleSignOut,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return context;
};